import React from 'react';
import {Body} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {CampaignTrackingUrl} from '@/types/campaign';
import BriefFieldInput from '@/views/campaigns/brief/components/brief-field-input';
import BriefItem from '@/views/campaigns/brief/components/campaign-brief/components/brief-item';
import ValidatedField from '@/views/campaigns/brief/components/campaign-brief/components/validated-field';
import {SectionProps} from '@/views/campaigns/brief/components/campaign-brief/section/SectionProps';
import QuestionsForCreator from '@/views/campaigns/brief/components/campaign-brief/section/additional-information/questions-for-creator';
import removeBriefItemSuffix from '@/views/campaigns/brief/utils/removeBriefItemSuffix';
import styles from './AdditionalInformation.module.scss';

const TRANSLATION_PREFIX =
  'components.campaign-brief.sections.additional-information';

const URL_PREFIX = 'https://';

function TrackingUrlRow({
  trackingUrl,
  index,
  editable,
  onChange,
  onRemove,
}: {
  trackingUrl: CampaignTrackingUrl;
  index: number;
  editable: boolean;
  onChange: (index: number, field: 'label' | 'url', value: string) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className={styles.trackingUrlRow}>
      <BriefFieldInput
        className={styles.trackingUrlLabel}
        label={index === 0 ? translate('tracking-url-label') : undefined}
        disabled={!editable}
        value={trackingUrl.label}
        placeholder={translate('tracking-url-label-placeholder')}
        onInputChange={(value: string) => onChange(index, 'label', value)}
        inputProps={{
          value: trackingUrl.label,
        }}
      />
      <BriefFieldInput
        className={styles.trackingUrlInput}
        label={index === 0 ? translate('tracking-url-url') : undefined}
        disabled={!editable}
        value={trackingUrl.url.replace(new RegExp(`^${URL_PREFIX}`, 'i'), '')}
        placeholder={translate('tracking-url-url-placeholder')}
        onInputChange={(value: string) =>
          onChange(index, 'url', value ? `${URL_PREFIX}${value}` : '')
        }
        inputProps={{
          value: trackingUrl.url.replace(new RegExp(`^${URL_PREFIX}`, 'i'), ''),
        }}
        startIcon={
          <Body size="md" className={styles.urlToPromotePrefix}>
            {URL_PREFIX}
          </Body>
        }
      />
      {editable && (
        <button
          type="button"
          className={styles.removeButton}
          onClick={() => onRemove(index)}
          aria-label={translate('tracking-url-remove')}
        >
          ✕
        </button>
      )}
    </div>
  );
}

function AdditionalInformation({
  editable,
  updateBriefForm,
  saveCampaign,
  briefFieldManager,
  errors,
  campaign,
}: SectionProps) {
  const trackingUrls: CampaignTrackingUrl[] =
    briefFieldManager.fields.campaignTrackingUrls?.value || [];

  const update = (newUrls: CampaignTrackingUrl[]) => {
    updateBriefForm?.('campaignTrackingUrls', newUrls, 'campaignTrackingUrls');
  };

  const handleChange = (
    index: number,
    field: 'label' | 'url',
    value: string
  ) => {
    const updated = [...trackingUrls];
    updated[index] = {...updated[index], [field]: value};
    update(updated);
  };

  const handleAdd = () => {
    update([...trackingUrls, {label: '', url: ''}]);
  };

  const handleRemove = (index: number) => {
    const updated = [...trackingUrls];
    updated.splice(index, 1);
    update(updated);
  };

  return (
    <div className={styles.container}>
      <BriefItem
        editable={editable}
        optional
        briefItem={briefFieldManager.fields.brandStyleGuideBriefItem?.value}
        saveCampaign={saveCampaign}
        onChange={(value) =>
          updateBriefForm?.(
            `campaignPaidGigBriefItems.${briefFieldManager.fields.brandStyleGuideBriefItem?.index}.contentHtml`,
            value,
            'brandStyleGuideBriefItem'
          )
        }
        placeholder={translate(
          `${TRANSLATION_PREFIX}.brand-style-guide-placeholder`
        )}
        campaignId={campaign.id}
        fieldName={removeBriefItemSuffix(
          briefFieldManager.fields.brandStyleGuideBriefItem?.key
        )}
      />
      {!briefFieldManager.fields.sectionsToHide.value?.urlToPromote ? (
        <ValidatedField errors={errors?.campaignTrackingUrls}>
          <div className={styles.trackingUrlsSection}>
            <Body size="lg" className={styles.trackingUrlsTitle}>
              {translate(`${TRANSLATION_PREFIX}.url-to-promote`)}
            </Body>
            <Body size="sm" className={styles.trackingUrlsDescription}>
              {translate('tracking-url-description')}
            </Body>
            {trackingUrls.map((tu, index) => (
              <TrackingUrlRow
                key={tu.id || `new-${index}`}
                trackingUrl={tu}
                index={index}
                editable={!!editable}
                onChange={handleChange}
                onRemove={handleRemove}
              />
            ))}
            {editable && (
              <button
                type="button"
                className={styles.addButton}
                onClick={handleAdd}
              >
                {translate('tracking-url-add')}
              </button>
            )}
          </div>
        </ValidatedField>
      ) : null}
      <BriefItem
        editable={editable}
        optional
        briefItem={
          briefFieldManager.fields.eligibilityRequirementsBriefItem?.value
        }
        saveCampaign={saveCampaign}
        onChange={(value) =>
          updateBriefForm?.(
            `campaignPaidGigBriefItems.${briefFieldManager.fields.eligibilityRequirementsBriefItem?.index}.contentHtml`,
            value,
            'eligibilityRequirementsBriefItem'
          )
        }
        placeholder={translate(
          `${TRANSLATION_PREFIX}.eligibility-requirements-placeholder`
        )}
        campaignId={campaign.id}
        fieldName={removeBriefItemSuffix(
          briefFieldManager.fields.eligibilityRequirementsBriefItem?.key
        )}
      />
      <QuestionsForCreator
        updateBriefForm={updateBriefForm}
        questions={briefFieldManager.fields.questions.value}
        editable={editable}
      />
    </div>
  );
}

export default AdditionalInformation;
